import React, { createContext, useContext, useState, useEffect } from 'react';
import CommonObj from '../CommonObj';
import { encryptTimestamp } from '../components/commoncsrf/csrf-common';
import { useAuthToken } from "../components/usecontext/AuthContext";

const AuthContext = createContext();

const AUDIT_LOG_CONFIG_STORAGE_KEY = 'auditLogConfig';

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userRoles, setUserRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rolePagePermissions, setRolePagePermissions] = useState(null);
  const { accessToken } = useAuthToken();

const roleUserId = userRoles.length > 0 ? userRoles[0].userId : 2;
const displayName = userRoles.length > 0 && userRoles[0].userName;
const menuname = userRoles.length > 0 && userRoles[0].menuname;
const tilename = userRoles.length > 0 && userRoles[0].tilename;
  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    const storedRoles = localStorage.getItem('userRoles');
   
    if (storedUser && storedRoles) {
      setUser(JSON.parse(storedUser));
      setUserRoles(JSON.parse(storedRoles));
    }
    setLoading(false);
  }, []);

  const setUserWithRoles = (userData, roles) => {
    setUser(userData);
    setUserRoles(roles);
    localStorage.setItem('user', JSON.stringify(userData));
    localStorage.setItem('userRoles', JSON.stringify(roles));
  };

  const fetchAuditLogConfig = async () => {
    try {
      const response = await fetch(CommonObj?.Endpoints?.GetAuditLogConfig, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${accessToken || ""}`,
          'X-CSRF-Token': encryptTimestamp(),
        },
      });
      const config = await response.json();
      const configRecord = Array.isArray(config) ? config[0] : config;

      localStorage.setItem(
        AUDIT_LOG_CONFIG_STORAGE_KEY,
        JSON.stringify({ value: configRecord, fetchedAt: Date.now() })
      );

      return configRecord;
    } catch (error) {
      console.warn('GetAuditLogConfig failed during login:', error);
      return null;
    }
  };

  const fetchRoleBasedPages = async () => {
    try {
      const response = await fetch(CommonObj?.Endpoints?.GetRoleBasedPages, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken || ""}`,
          'X-CSRF-Token': encryptTimestamp(),
        },
      });

      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

      const data = await response.json();
      const pages = Array.isArray(data) ? data : [];
      setRolePagePermissions(pages);
      return pages;
    } catch (error) {
      console.warn('GetRoleBasedPages failed during login:', error);
      setRolePagePermissions([]);
      return [];
    }
  };

  useEffect(() => {
  if (!user || !accessToken) return;
    fetchRoleBasedPages();
  }, [user, accessToken]);

  const logout = () => {
    setUser(null);
    setUserRoles([]);
    localStorage.removeItem('user');
    localStorage.removeItem('userRoles');
  };

  const hasRole = (roleName) => {
    const normalizedRoleName = (roleName || '').trim().toLowerCase();
    if (!normalizedRoleName) return false;

    const userRoleNames = userRoles.flatMap((role) =>
      (role?.roleName || '')
        .split(',')
        .map((item) => item.trim().toLowerCase())
        .filter(Boolean)
    );

    return userRoleNames.includes(normalizedRoleName);
  };

  const hasAnyRole = (roleNames = []) => {
    return roleNames.some((roleName) => hasRole(roleName));
  };

  const value = {
    user,
    userRoles,
    logout,
    hasRole,
    hasAnyRole,
    loading,
    isAuthenticated: !!user,
    setUserWithRoles, 
     roleUserId,
     displayName,
    menuname,
     tilename,
     fetchAuditLogConfig,
     rolePagePermissions,
     fetchRoleBasedPages
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;
